import { Link, NavLink, Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useEffect, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Freshness } from '@/components/Freshness'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'
import { useLocale } from '@/i18n/LocaleProvider'
import { getAlertsUnreadCount, getDashboard, getExtractionQueueCount, getOrders, getReconBoard } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { CURRENT_STAGE } from '@/lib/stage'
import curlyLogo from '@/assets/brand/curly-logo.jpg'
import { DealSheet } from '@/components/DealSheet'
import { LIMITS_SCROLL_EVENT, scrollToCabinAnchor } from '@/lib/scroll'
import { isNativeApp } from '@/lib/native'

interface NavItem {
  to: string
  label: string
  stage: number
  icon?: IconName
  hash?: string
}

type IconName = 'today' | 'orders' | 'limit' | 'rates' | 'people' | 'settings' | 'profit'

function cabinItems(t: (key: string) => string): NavItem[] {
  return [
    { to: '/', label: t('nav.cabinFull'), stage: 1, icon: 'today' },
    { to: '/orders', label: t('nav.orders'), stage: 1, icon: 'orders' },
    { to: '/pnl', label: t('nav.profit'), stage: 3, icon: 'profit' },
    { to: '/', label: t('nav.bankLimit'), stage: 1, icon: 'limit', hash: '#ліміти' },
    { to: '/', label: t('nav.rates'), stage: 1, icon: 'rates', hash: '#курси' },
    { to: '/counterparties', label: t('nav.clients'), stage: 1, icon: 'people' },
    { to: '/settings', label: t('nav.settings'), stage: 1, icon: 'settings' },
  ]
}

function moreSections(t: (key: string) => string): { title: string; items: NavItem[] }[] {
  return [
    {
      title: t('nav.ops'),
      items: [
        { to: '/import', label: t('nav.import'), stage: 1 },
        { to: '/screenshots', label: t('nav.screenshots'), stage: 2 },
      ],
    },
    {
      title: t('nav.money'),
      items: [
        { to: '/accounts', label: t('nav.accounts'), stage: 1 },
        { to: '/transactions', label: t('nav.movements'), stage: 4 },
        { to: '/reconciliation', label: t('nav.recon'), stage: 5 },
      ],
    },
    {
      title: t('nav.books'),
      items: [
        { to: '/inventory', label: t('nav.inventory'), stage: 3 },
        { to: '/reports', label: t('nav.reports'), stage: 6 },
      ],
    },
    {
      title: t('nav.control'),
      items: [
        { to: '/alerts', label: t('nav.alerts'), stage: 7 },
        { to: '/activity', label: t('nav.activity'), stage: 7 },
      ],
    },
  ]
}

export function Layout({ children }: { children?: ReactNode }) {
  const { me, logout } = useAuth()
  const { t } = useLocale()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const dealOpen = searchParams.get('deal') === '1'
  const [navOpen, setNavOpen] = useState(false)
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches,
  )
  const [online, setOnline] = useState(true)
  const queue = useQuery({
    queryKey: ['extraction', 'queue-count'],
    queryFn: getExtractionQueueCount,
    refetchInterval: 10_000,
    enabled: CURRENT_STAGE >= 2,
  })
  const recon = useQuery({
    queryKey: ['reconciliation'],
    queryFn: getReconBoard,
    refetchInterval: 20_000,
    enabled: CURRENT_STAGE >= 5,
  })
  const alerts = useQuery({
    queryKey: ['alerts', 'unread'],
    queryFn: getAlertsUnreadCount,
    refetchInterval: 15_000,
    enabled: CURRENT_STAGE >= 7,
  })
  const openOrders = useQuery({
    queryKey: ['orders', 'open-count'],
    queryFn: () => getOrders({ lifecycle: 'open', limit: '1' }),
    refetchInterval: 20_000,
  })
  const openOrderCount = openOrders.data?.total ?? 0
  const dashToday = useQuery({
    queryKey: ['dashboard', 'today'],
    queryFn: () => getDashboard({ period: 'today' }),
    staleTime: 15_000,
  })
  const hotLimit = (dashToday.data?.limits ?? [])
    .slice()
    .sort((a, b) => Number(b.pct) - Number(a.pct))[0]
  const hotPct = hotLimit ? Math.round(Number(hotLimit.pct) * 100) : null

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)')
    const sync = () => {
      setIsDesktop(mq.matches)
      if (mq.matches) setNavOpen(false)
    }
    sync()
    mq.addEventListener('change', sync)
    return () => mq.removeEventListener('change', sync)
  }, [])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setNavOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  useEffect(() => {
    document.body.classList.toggle('overflow-hidden', navOpen)
    return () => document.body.classList.remove('overflow-hidden')
  }, [navOpen])

  useEffect(() => {
    const ping = () => {
      fetch('/api/v1/health/live', { credentials: 'include' })
        .then((res) => setOnline(res.ok))
        .catch(() => setOnline(false))
    }
    ping()
    const timer = window.setInterval(ping, 20_000)
    return () => window.clearInterval(timer)
  }, [])

  const closeNav = () => setNavOpen(false)
  const setDeal = (open: boolean) => {
    const next = new URLSearchParams(searchParams)
    if (open) next.set('deal', '1')
    else next.delete('deal')
    navigate(
      {
        pathname: location.pathname,
        search: next.toString() ? `?${next}` : '',
        hash: location.hash,
      },
      { replace: true },
    )
  }

  useEffect(() => {
    if (!isNativeApp()) return
    let removed = false
    let handle: { remove: () => Promise<void> } | undefined
    void import('@capacitor/app').then(({ App }) =>
      App.addListener('backButton', () => {
        if (dealOpen) {
          setDeal(false)
          return
        }
        if (navOpen) {
          setNavOpen(false)
          return
        }
        if (window.history.length > 1) window.history.back()
        else void App.minimizeApp()
      }).then((listener) => {
        if (removed) void listener.remove()
        else handle = listener
      }),
    )
    return () => {
      removed = true
      void handle?.remove()
    }
  }, [dealOpen, navOpen])

  const goLimits = () => {
    closeNav()
    const onLimits = location.pathname === '/' && decodeHash(location.hash) === '#ліміти'
    if (onLimits && searchParams.get('deal') !== '1') {
      window.dispatchEvent(new Event(LIMITS_SCROLL_EVENT))
      scrollToCabinAnchor('ліміти')
      return
    }
    navigate({ pathname: '/', search: '', hash: 'ліміти' })
  }
  const hideAside = !isDesktop && !navOpen
  const reconCount = (recon.data?.pending_review ?? 0) + (recon.data?.open_exceptions ?? 0)

  const badge = (to: string) => {
    if (to === '/orders' && openOrderCount > 0) {
      return <span className="num rounded bg-pending/20 px-1.5 text-[10px] text-pending">{openOrderCount}</span>
    }
    if (to === '/screenshots' && (queue.data?.review ?? 0) > 0) {
      return <span className="num rounded bg-pending/20 px-1.5 text-[10px] text-pending">{queue.data?.review}</span>
    }
    if (to === '/reconciliation' && reconCount > 0) {
      return <span className="num rounded bg-pending/20 px-1.5 text-[10px] text-pending">{reconCount}</span>
    }
    if (to === '/alerts' && (alerts.data?.count ?? 0) > 0) {
      return <span className="num rounded bg-loss/20 px-1.5 text-[10px] text-loss">{alerts.data?.count}</span>
    }
    return null
  }

  return (
    <div className="mma-shell flex h-dvh overflow-hidden">
      {navOpen ? (
        <button
          type="button"
          aria-label={t('nav.close')}
          className="fixed inset-0 z-30 bg-black/60 md:hidden"
          onClick={closeNav}
        />
      ) : null}

      <aside
        id="app-sidebar"
        aria-hidden={hideAside}
        {...(hideAside ? ({ inert: '' } as Record<string, string>) : {})}
        className={[
          'fixed inset-y-0 left-0 z-40 flex w-[min(17.5rem,88vw)] flex-col border-r border-surface-border transition-transform duration-200',
          'md:static md:z-auto md:w-60 md:max-w-none md:translate-x-0 md:pointer-events-auto',
          navOpen ? 'translate-x-0 pointer-events-auto' : '-translate-x-full pointer-events-none',
        ].join(' ')}
      >
        <div className="flex items-start justify-between gap-2 px-4 py-4">
          <div className="flex min-w-0 items-center gap-2.5">
            <img src={curlyLogo} alt="CURLY" className="brand-logo shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-semibold tracking-wide text-gold-bright">P2P Curly трекер</div>
              <div className="mt-1 flex items-center gap-1.5 text-[11px] text-slate-500">
                <span className={['h-1.5 w-1.5 rounded-full', online ? 'bg-profit' : 'bg-loss'].join(' ')} />
                {online ? t('nav.online') : t('nav.offline')}
              </div>
            </div>
          </div>
          <button
            type="button"
            className="inline-flex h-11 w-11 items-center justify-center rounded-md text-slate-400 hover:bg-slate-700/30 hover:text-slate-100 md:hidden"
            aria-label={t('nav.close')}
            onClick={closeNav}
          >
            <CloseIcon />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-2 pb-6">
          <div className="mb-4">
            {cabinItems(t).map((item) => {
              if (item.stage > CURRENT_STAGE) return <LockedItem key={item.label} item={item} />
              const href = item.hash ? `/${item.hash}` : item.to
              const isHash = Boolean(item.hash)
              const active = isHash
                ? location.pathname === '/' && decodeHash(location.hash) === item.hash
                : location.pathname === item.to && (item.to !== '/' || !location.hash)
              return (
                <Link
                  key={item.label}
                  to={href}
                  onClick={(event) => {
                    if (item.hash === '#ліміти') {
                      event.preventDefault()
                      goLimits()
                      return
                    }
                    closeNav()
                  }}
                  className={[
                    'flex min-h-11 items-center gap-2.5 rounded-md px-3 py-1.5 text-sm transition-colors',
                    active
                      ? 'mma-nav-active'
                      : 'text-slate-400 hover:bg-slate-700/20 hover:text-gold-bright',
                  ].join(' ')}
                >
                  {item.icon ? <NavIcon name={item.icon} /> : null}
                  <span className="flex-1">{item.label}</span>
                  {badge(item.to)}
                </Link>
              )
            })}
          </div>

          {moreSections(t).map((section) => (
            <div key={section.title} className="mb-5">
              <div className="px-3 pb-1.5 text-[11px] font-medium uppercase tracking-wider text-gold/60">
                {section.title}
              </div>
              {section.items.map((item) =>
                item.stage <= CURRENT_STAGE ? (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={closeNav}
                    className={({ isActive }) =>
                      [
                        'flex min-h-11 items-center justify-between rounded-md px-3 py-1.5 text-sm transition-colors',
                        isActive
                          ? 'mma-nav-active'
                          : 'text-slate-400 hover:bg-slate-700/20 hover:text-gold-bright',
                      ].join(' ')
                    }
                  >
                    <span>{item.label}</span>
                    {badge(item.to)}
                  </NavLink>
                ) : (
                  <LockedItem key={item.to} item={item} />
                ),
              )}
            </div>
          ))}
        </nav>

        {me && (
          <div className="border-t border-surface-border px-4 py-4 text-xs text-slate-500">
            <div className="truncate text-slate-300">{me.organization.name}</div>
            <div className="truncate">{me.user.email}</div>
            <button
              type="button"
              className="mt-2 min-h-11 text-slate-400 hover:text-slate-200"
              onClick={() => {
                closeNav()
                void logout().then(() => navigate('/login'))
              }}
            >
              {t('nav.logout')}
            </button>
          </div>
        )}
      </aside>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <header className="mma-topbar sticky top-0 z-20 flex items-center gap-2 border-b border-surface-border px-3 py-2 md:gap-3 md:px-6 md:py-3">
          <button
            type="button"
            className="mma-menu-btn inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md border bg-surface-raised hover:bg-slate-700/40 md:hidden"
            aria-expanded={navOpen}
            aria-controls="app-sidebar"
            onClick={() => setNavOpen((open) => !open)}
          >
            <span className="sr-only">{t('nav.menu')}</span>
            <MenuIcon />
          </button>
          <div className="mma-top-title min-w-0 flex-1 truncate text-sm font-medium md:hidden">
            P2P Curly
          </div>
          {hotLimit && hotPct !== null ? (
            <button type="button" className="mma-limit-pill md:hidden" onClick={goLimits}>
              <span className="max-w-[7rem] truncate">{hotLimit.name}</span>
              <span className="num">{hotPct}%</span>
            </button>
          ) : null}
          <div className="hidden min-w-0 flex-1 overflow-x-auto md:block">
            <Freshness />
          </div>
          <LanguageSwitcher compact />
        </header>
        <main className="mma-main min-h-0 flex-1 overflow-auto">{children ?? <Outlet />}</main>
        <MobileTabBar
          pathname={location.pathname}
          hash={location.hash}
          dealOpen={dealOpen}
          onPlus={() => setDeal(!dealOpen)}
          onLimits={goLimits}
          ordersBadge={openOrderCount}
        />
        {dealOpen ? <DealSheet onClose={() => setDeal(false)} /> : null}
      </div>
    </div>
  )
}

function decodeHash(hash: string) {
  try {
    return decodeURIComponent(hash)
  } catch {
    return hash
  }
}

function MobileTabBar({
  pathname,
  hash,
  dealOpen,
  onPlus,
  onLimits,
  ordersBadge,
}: {
  pathname: string
  hash: string
  dealOpen: boolean
  onPlus: () => void
  onLimits: () => void
  ordersBadge: number
}) {
  const { t } = useLocale()
  const decodedHash = decodeHash(hash)
  const cabinOn = !dealOpen && pathname === '/' && decodedHash !== '#ліміти'
  const ordersOn = !dealOpen && (pathname === '/orders' || (pathname.startsWith('/orders/') && pathname !== '/orders/new'))
  const limitsOn = !dealOpen && pathname === '/' && decodedHash === '#ліміти'
  const marketOn = !dealOpen && pathname === '/market'

  return (
    <nav className="mma-tabbar md:hidden" aria-label={t('nav.quick')}>
      <TabLink to="/" label={t('nav.cabin')} active={cabinOn}>
        <TabIcon name="cabin" />
      </TabLink>
      <TabLink to="/orders" label={t('nav.orders')} active={ordersOn} badge={ordersBadge}>
        <TabIcon name="orders" />
      </TabLink>
      <button
        type="button"
        className={['mma-tab', 'mma-tab-plus', dealOpen ? 'mma-tab-on' : ''].join(' ')}
        aria-current={dealOpen ? 'page' : undefined}
        onClick={onPlus}
      >
        <TabIcon name="plus" />
        <span>+</span>
      </button>
      <button
        type="button"
        className={['mma-tab', limitsOn ? 'mma-tab-on' : ''].join(' ')}
        aria-current={limitsOn ? 'page' : undefined}
        onClick={onLimits}
      >
        <TabIcon name="limit" />
        <span>{t('nav.limits')}</span>
      </button>
      <TabLink to="/market" label={t('nav.market')} active={marketOn}>
        <TabIcon name="market" />
      </TabLink>
    </nav>
  )
}

function TabLink({
  to,
  label,
  active,
  plus,
  badge,
  children,
}: {
  to: string
  label: string
  active: boolean
  plus?: boolean
  badge?: number
  children: ReactNode
}) {
  return (
    <Link
      to={to}
      className={[
        'mma-tab',
        plus ? 'mma-tab-plus' : '',
        active ? 'mma-tab-on' : '',
      ].join(' ')}
      aria-current={active ? 'page' : undefined}
    >
      {children}
      <span className="inline-flex items-center gap-1">
        {plus ? '+' : label}
        {badge ? <span className="mma-tab-badge">{badge}</span> : null}
      </span>
    </Link>
  )
}

function TabIcon({ name }: { name: 'cabin' | 'orders' | 'plus' | 'limit' | 'market' }) {
  const path =
    name === 'cabin'
      ? 'M4 10.5 12 4l8 6.5V20a1 1 0 0 1-1 1h-5v-6H10v6H5a1 1 0 0 1-1-1V10.5Z'
      : name === 'orders'
        ? 'M7 7h10M7 12h10M7 17h6'
        : name === 'plus'
          ? 'M12 6v12M6 12h12'
          : name === 'limit'
            ? 'M5 8h14v8H5zM5 12h14'
            : 'M5 16l4.5-6 3.5 3 6-8'
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d={path} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function LockedItem({ item }: { item: NavItem }) {
  const { t } = useLocale()
  return (
    <div
      className="flex min-h-11 items-center justify-between rounded-md px-3 py-1.5 text-sm text-slate-600"
      title={t('nav.locked', { stage: item.stage })}
    >
      <span>{item.label}</span>
      <span className="num text-[10px] text-slate-700">Е{item.stage}</span>
    </div>
  )
}

function NavIcon({ name }: { name: IconName }) {
  const path =
    name === 'today'
      ? 'M4 8h16M7 4v4m10-4v4M6 8v10a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V8'
      : name === 'orders'
        ? 'M6 7h12M6 12h12M6 17h8'
        : name === 'limit'
          ? 'M4 8h16v8H4zM4 12h16'
          : name === 'rates'
            ? 'M5 16l5-6 4 3 5-7'
            : name === 'people'
              ? 'M12 12a3.5 3.5 0 1 0-0.01-7A3.5 3.5 0 0 0 12 12Zm-7 8a5 5 0 0 1 10 0'
              : name === 'profit'
                ? 'M5 16l5-7 4 4 5-8M14 5h5v5'
                : 'M12 8.5a3.5 3.5 0 1 0 .01 7 3.5 3.5 0 0 0-.01-7Zm7.5 3.5h.5M4 12h.5M16.8 7.2l.4-.4M6.8 17.2l.4-.4M16.8 16.8l.4.4M6.8 6.8l.4.4'
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true" className="shrink-0">
      <path d={path} stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function MenuIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  )
}

function CloseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  )
}
